import clsx from 'clsx';
import { FaChevronDown, FaCrown } from 'react-icons/fa';
import { GiMagicSwirl } from 'react-icons/gi';
import { IoWarning } from 'react-icons/io5';
import { DECK_COLUMN_LABELS, DECK_COLUMN_ORDER, type DeckColumnKey, type DeckColumnVisibility } from '../state/deckEditorSettings';

/** Column headers that render as separate grid cells */
const CELL_COLUMNS: DeckColumnKey[] = [
  'supertype',
  'type',
  'subtype',
  'fullType',
  'manaCost',
  'cmc',
  'colorIdentity',
  'manaProduced',
  'rarity',
  'powerToughness',
  'power',
  'toughness',
  'price',
  'set',
  'setNumber',
  'collectorNumber',
  'released',
  'roles',
  'keywords',
];

/** Columns that should right-align their headers (to match cell content alignment) */
const RIGHT_ALIGN_COLUMNS: DeckColumnKey[] = [
  'supertype',
  'type',
  'subtype',
  'fullType',
  'manaCost',
  'price',
  'set',
  'setNumber',
  'collectorNumber',
  'released',
];

/** Columns that should center-align their headers */
const CENTER_ALIGN_COLUMNS: DeckColumnKey[] = ['cmc', 'rarity', 'powerToughness', 'power', 'toughness', 'colorIdentity', 'manaProduced'];

/**
 * Get alignment class for column header
 */
function getColumnHeaderAlignClass(column: DeckColumnKey): string {
  if (RIGHT_ALIGN_COLUMNS.includes(column)) return 'justify-end';
  if (CENTER_ALIGN_COLUMNS.includes(column)) return 'justify-center';
  return 'justify-start'; // roles
}

export interface SectionTitleProps {
  title?: string;
  /** Number of unique cards */
  uniqueCount?: number;
  /** Total card quantity (including copies) */
  totalCount?: number;
  isCommander?: boolean;
  isCompanion?: boolean;
  /** Cards in this section are in a zone not available for the current format/status */
  isOrphaned?: boolean;
  isExpanded?: boolean;
  onClick?: () => void;
  /** Column visibility settings for rendering column headers (list view only) */
  columnVisibility?: DeckColumnVisibility;
}

/**
 * SectionTitle - Grid-aware section header for deck list view
 *
 * Uses `display: contents` to properly participate in the parent CSS Grid.
 * When columnVisibility is provided, renders individual grid cells that align
 * with the data columns below.
 *
 * Grid structure (when columnVisibility provided):
 * - Title cell spans first 3 columns (qty, img, name)
 * - Each visible dynamic column gets its own header cell
 * - Actions cell is empty
 *
 * Supports:
 * - Commander variant with amber styling
 * - Companion variant with purple styling
 * - Collapsible sections with smooth chevron rotation animation
 * - Static non-collapsible headers
 * - Column headers for list view (when columnVisibility is provided)
 */
export default function SectionTitle({
  title,
  uniqueCount,
  totalCount,
  isCommander = false,
  isCompanion = false,
  isOrphaned = false,
  isExpanded = true,
  onClick,
  columnVisibility,
}: SectionTitleProps) {
  title = title || (isCommander ? 'Commander' : isCompanion ? 'Companion' : 'Section');

  // Build the count display: "9 (9)" format like stacked view
  const countDisplay = uniqueCount !== undefined && totalCount !== undefined ? `${uniqueCount} (${totalCount})` : '';

  // Get visible column headers for list view (only columns that render as grid cells)
  const visibleColumnHeaders = columnVisibility
    ? DECK_COLUMN_ORDER.filter((col) => columnVisibility[col] && CELL_COLUMNS.includes(col))
    : [];

  // Commander variant - special styling with crown icon
  if (isCommander) {
    // If column visibility provided, render as grid cells
    if (columnVisibility && visibleColumnHeaders.length > 0) {
      const cellBase = 'mb-3 py-2 px-1 border-b border-amber-500/30';
      return (
        <div className="contents">
          {/* Title cell - spans first 3 columns (qty, img, name) */}
          <div style={{ gridColumn: 'span 3' }} className={clsx(cellBase, 'flex items-center gap-2')}>
            <FaCrown className="h-4 w-4 text-amber-400" />
            <h3 className="text-sm font-semibold tracking-wider text-amber-300">{title}</h3>
            {isOrphaned && (
              <IoWarning
                className="h-4 w-4 text-amber-400"
                title="This zone is not available for the current format — move or remove these cards"
              />
            )}
          </div>

          {/* Column header cells */}
          {visibleColumnHeaders.map((col) => (
            <div key={col} className={clsx(cellBase, 'flex overflow-hidden', getColumnHeaderAlignClass(col), 'items-center')}>
              <span className="text-[10px] font-medium tracking-wide text-amber-500/60">{DECK_COLUMN_LABELS[col]}</span>
            </div>
          ))}

          {/* Actions cell - empty */}
          <div className={cellBase} />
        </div>
      );
    }

    // Fallback: col-span-full
    return (
      <div className="contents">
        <div className="col-span-full mb-3 flex w-full items-center gap-2 border-b border-amber-500/30 pb-2">
          <FaCrown className="h-4 w-4 text-amber-400" />
          <h3 className="text-sm font-semibold tracking-wider text-amber-300">{title}</h3>
          {isOrphaned && (
            <IoWarning
              className="h-4 w-4 text-amber-400"
              title="This zone is not available for the current format — move or remove these cards"
            />
          )}
        </div>
      </div>
    );
  }

  // Companion variant - special styling with companion icon
  if (isCompanion) {
    // If column visibility provided, render as grid cells
    if (columnVisibility && visibleColumnHeaders.length > 0) {
      const cellBase = 'mb-3 py-2 px-1 border-b border-purple-500/30';
      return (
        <div className="contents">
          {/* Title cell - spans first 3 columns (qty, img, name) */}
          <div style={{ gridColumn: 'span 3' }} className={clsx(cellBase, 'flex items-center gap-2')}>
            <GiMagicSwirl className="h-4 w-4 text-purple-400" />
            <h3 className="text-sm font-semibold tracking-wider text-purple-300">{title}</h3>
          </div>

          {/* Column header cells */}
          {visibleColumnHeaders.map((col) => (
            <div key={col} className={clsx(cellBase, 'flex overflow-hidden', getColumnHeaderAlignClass(col), 'items-center')}>
              <span className="text-[10px] font-medium tracking-wide text-purple-500/60">{DECK_COLUMN_LABELS[col]}</span>
            </div>
          ))}

          {/* Actions cell - empty */}
          <div className={cellBase} />
        </div>
      );
    }

    // Fallback: col-span-full
    return (
      <div className="contents">
        <div className="col-span-full mb-3 flex w-full items-center gap-2 border-b border-purple-500/30 pb-2">
          <GiMagicSwirl className="h-4 w-4 text-purple-400" />
          <h3 className="text-sm font-semibold tracking-wider text-purple-300">{title}</h3>
        </div>
      </div>
    );
  }

  // If we have column visibility, render as grid cells aligned with data columns
  if (columnVisibility && visibleColumnHeaders.length > 0) {
    const cellBase = 'mt-3 mb-2 py-2 px-1 border-b border-zinc-800';

    // Collapsible variant with grid-aligned column headers
    if (onClick) {
      return (
        <div className="group/header contents">
          {/* Title cell - spans first 3 columns (qty, img, name) */}
          <button
            type="button"
            onClick={onClick}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title} section`}
            style={{ gridColumn: 'span 3' }}
            className={clsx(
              cellBase,
              'focus-visible:ring-aether flex items-center gap-2 rounded-l-lg text-left transition-colors hover:bg-zinc-800/30 focus:outline-none focus-visible:ring-2',
            )}
          >
            <FaChevronDown
              className={clsx('h-3 w-3 text-zinc-500 transition-transform duration-200 ease-out', !isExpanded && '-rotate-90')}
            />
            <h3 className="text-sm font-semibold tracking-wider text-zinc-300 uppercase">{title}</h3>
            {countDisplay && (
              <>
                <span className="font-heading text-sm font-semibold tracking-wider text-zinc-600">—</span>
                <span className="text-xs text-zinc-500">{countDisplay}</span>
              </>
            )}
          </button>

          {/* Column header cells - one per visible column */}
          {visibleColumnHeaders.map((col) => (
            <div
              key={col}
              className={clsx(
                cellBase,
                'flex overflow-hidden',
                getColumnHeaderAlignClass(col),
                'items-center transition-colors group-hover/header:bg-zinc-800/30',
              )}
            >
              <span className="text-[10px] font-medium tracking-wide text-zinc-600">{DECK_COLUMN_LABELS[col]}</span>
            </div>
          ))}

          {/* Actions cell - empty, completes the row */}
          <div className={clsx(cellBase, 'rounded-r-lg transition-colors group-hover/header:bg-zinc-800/30')} />
        </div>
      );
    }

    // Static variant with grid-aligned column headers
    return (
      <div className="contents">
        {/* Title cell - spans first 3 columns (qty, img, name) */}
        <div style={{ gridColumn: 'span 3' }} className={clsx(cellBase, 'flex items-center gap-2')}>
          <h3 className="text-sm font-semibold tracking-wider text-zinc-300 uppercase">{title}</h3>
          {countDisplay && (
            <>
              <span className="font-heading text-sm font-semibold tracking-wider text-zinc-600">—</span>
              <span className="text-xs text-zinc-500">{countDisplay}</span>
            </>
          )}
        </div>

        {/* Column header cells */}
        {visibleColumnHeaders.map((col) => (
          <div key={col} className={clsx(cellBase, 'flex overflow-hidden', getColumnHeaderAlignClass(col), 'items-center')}>
            <span className="text-[10px] font-medium tracking-wide text-zinc-600">{DECK_COLUMN_LABELS[col]}</span>
          </div>
        ))}

        {/* Actions cell - empty */}
        <div className={cellBase} />
      </div>
    );
  }

  // Fallback: col-span-full (for non-list views or when no column visibility)
  if (onClick) {
    return (
      <div className="contents">
        <button
          type="button"
          onClick={onClick}
          aria-expanded={isExpanded}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title} section`}
          className="focus-visible:ring-aether col-span-full mt-3 mb-2 flex w-full items-center justify-between rounded-lg border-b border-zinc-800 px-2 py-2 text-left transition-colors hover:bg-zinc-800/30 focus:outline-none focus-visible:ring-2"
        >
          <div className="flex items-center gap-2">
            <FaChevronDown
              className={clsx('h-3 w-3 text-zinc-500 transition-transform duration-200 ease-out', !isExpanded && '-rotate-90')}
            />
            <h3 className="text-sm font-semibold tracking-wider text-zinc-300 uppercase">{title}</h3>
          </div>
          {countDisplay && <span className="text-xs text-zinc-500">{countDisplay}</span>}
        </button>
      </div>
    );
  }

  // Static fallback
  return (
    <div className="contents">
      <div className="col-span-full mt-3 mb-2 flex w-full items-center justify-between border-b border-zinc-800 pb-2">
        <h3 className="text-sm font-semibold tracking-wider text-zinc-300 uppercase">{title}</h3>
        {countDisplay && <span className="text-xs text-zinc-500">{countDisplay}</span>}
      </div>
    </div>
  );
}
